import { formatISO, parse } from 'date-fns';
import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '~/contexts/newToastContext';
import { DayProg, FullProg_v4 } from '~/providers/types';
import { ScheduleList } from '../../../components/ScheduleList';
import { parseDamProgramming } from '../../helpers';
import { Exception, Schedule } from '../../types';
import { DeletionConfirmationModal } from '../ConfirmationModal';
import { DamExceptionViewCard } from '../ExceptionCard';
import { DamScheduleViewCard } from '../ScheduleCard';
import { DamScheduleFormValues } from '../ScheduleForm/types';
import { DamScheduleFormModal } from '../ScheduleFormModal';
import { PendingBadge } from './PendingBadge';

interface DamScheduleListProps {
  lastProg: FullProg_v4;
  fixed: boolean;
  onChangeProg: (prog: FullProg_v4) => void;
  onlyList: boolean;
  disabled?: boolean;
  pendingProg?: boolean;
}

export function DamScheduleList({
  lastProg,
  fixed,
  onlyList,
  onChangeProg,
  disabled,
  pendingProg,
}: Readonly<DamScheduleListProps>): ReactElement {
  const { t } = useTranslation();
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [deletionTarget, setDeletionTarget] = useState<
    { type: 'exception'; item: Exception } | { type: 'schedule'; item: Schedule } | null
  >(null);
  const [typeForm, setTypeForm] = useState<'schedule' | 'exception'>('schedule');
  const [defaultValues, setDefaultValues] = useState<DamScheduleFormValues | undefined>();
  const { schedules, exceptions } = parseDamProgramming(lastProg);
  const { toast } = useToast();

  const hide = {
    delete: onlyList,
    edit: onlyList,
  };

  function onCloseFormModal(): void {
    setShowScheduleModal(false);
  }

  function onCloseConfirmModal(): void {
    setShowConfirmModal(false);
  }

  function onClickDeleteException(exception: Exception): void {
    setDeletionTarget({ type: 'exception', item: exception });
    setShowConfirmModal(true);
  }

  function onClickDeleteSchedule(schedule: Schedule): void {
    setDeletionTarget({ type: 'schedule', item: schedule });
    setShowConfirmModal(true);
  }

  function onConfirmDeletion(): void {
    if (!deletionTarget) return;

    if (deletionTarget.type === 'exception') {
      const newExceptions = { ...lastProg.exceptions };
      delete newExceptions[deletionTarget.item.date];
      onChangeProg({ ...lastProg, exceptions: newExceptions });
    }

    if (deletionTarget.type === 'schedule') {
      const newWeek = { ...lastProg.week };
      for (const [day, active] of Object.entries(deletionTarget.item.days)) {
        if (active) delete newWeek[day];
      }
      onChangeProg({ ...lastProg, week: newWeek });
    }

    setDeletionTarget(null);
    onCloseConfirmModal();
  }

  function onClickAddButton(type: 'schedules' | 'exceptions'): void {
    if (defaultValues) setDefaultValues(undefined);
    setTypeForm(type === 'schedules' ? 'schedule' : 'exception');
    setShowScheduleModal(true);
  }

  function onClickEditException(exception: Exception): void {
    setDefaultValues({
      endTime: exception.endTime,
      exceptionDate: exception.date,
      startTime: exception.beginTime,
      permission: exception.permission,
      fullDay: exception.endTime === '23:59' && exception.beginTime === '00:00',
      weekDays: [],
    });
    setTypeForm('exception');
    setShowScheduleModal(true);
  }

  function onClickEditSchedule(schedule: Schedule): void {
    setDefaultValues({
      endTime: schedule.endTime,
      startTime: schedule.beginTime,
      permission: schedule.permission,
      fullDay: schedule.endTime === '23:59' && schedule.beginTime === '00:00',
      weekDays: Object.entries(schedule.days).filter(([, value]) => !!value).map(([key]) => key),
      exceptionDate: '',
    });
    setTypeForm('schedule');
    setShowScheduleModal(true);
  }

  function onSuccess(data: DamScheduleFormValues, type: 'schedule' | 'exception'): void {
    if (type === 'exception') {
      if (isOverwritingException(data)) {
        toast(t('erroTitulo'), t('jaExisteExcecaoData'), 'error');
        return;
      }
      const newProg = handleExceptionProg(data);
      onChangeProg(newProg);
    }

    if (type === 'schedule') {
      if (isOverwritingSchedule(data)) {
        toast(t('erroTitulo'), t('jaExisteProgramacaoDia'), 'error');
        return;
      }
      const newProg = handleScheduleProg(data);
      onChangeProg(newProg);
    }

    onCloseFormModal();
    if (defaultValues) setDefaultValues(undefined);
  }

  function isOverwritingException(data: DamScheduleFormValues): boolean {
    const exceptions = lastProg.exceptions ?? {};
    const formattedDate = formatISO(parse(data.exceptionDate, 'dd/MM/yyyy', new Date()), { representation: 'date' });
    const isEditing = !!defaultValues;
    const dateAlreadyExists = formattedDate in exceptions;

    if (!dateAlreadyExists) return false;
    if (!isEditing) return true;

    return formattedDate !== defaultValues.exceptionDate;
  }

  function isOverwritingSchedule(data: DamScheduleFormValues): boolean {
    const isEditing = !!defaultValues;

    for (const day of data.weekDays) {
      const dayAlreadyExists = !!lastProg.week[day];

      if (!dayAlreadyExists) continue;
      if (!isEditing) return true;
      if (!defaultValues.weekDays.includes(day)) return true;
    }

    return false;
  }

  function handleExceptionProg(data: DamScheduleFormValues): FullProg_v4 {
    const newProg = { ...lastProg };
    const currentExceptions = { ...lastProg.exceptions };
    const formattedDate = formatISO(parse(data.exceptionDate, 'dd/MM/yyyy', new Date()), { representation: 'date' });

    const newException: DayProg = {
      start: data.fullDay ? '00:00' : data.startTime,
      end: data.fullDay ? '23:59' : data.endTime,
      permission: data.permission,
    };

    if (defaultValues) {
      delete currentExceptions[defaultValues.exceptionDate];
    }

    return {
      ...newProg,
      exceptions: {
        ...currentExceptions,
        [formattedDate]: newException,
      },
    };
  }

  function handleScheduleProg(data: DamScheduleFormValues): FullProg_v4 {
    const newProg = { ...lastProg, week: { ...lastProg.week } };
    const newSched: DayProg = {
      start: data.fullDay ? '00:00' : data.startTime,
      end: data.fullDay ? '23:59' : data.endTime,
      permission: data.permission,
    };

    if (defaultValues) {
      for (const day of defaultValues.weekDays) {
        delete newProg.week[day];
      }
    }

    for (const day of data.weekDays) {
      newProg.week[day] = newSched;
    }

    return newProg;
  }

  return (
    <>
      <DeletionConfirmationModal
        open={showConfirmModal}
        onClose={onCloseConfirmModal}
        onConfirm={onConfirmDeletion}
        type={deletionTarget?.type ?? 'schedule'}
      />
      <DamScheduleFormModal
        defaultValues={defaultValues}
        open={showScheduleModal}
        onClose={onCloseFormModal}
        onSuccess={onSuccess}
        type={typeForm}
      />
      <ScheduleList
        fixed={fixed}
        exceptions={exceptions}
        schedules={schedules}
        renderExceptionCard={(exception, index) => (
          <DamExceptionViewCard
            exception={exception}
            key={index}
            onDelete={() => onClickDeleteException(exception)}
            onEdit={() => onClickEditException(exception)}
            hide={hide}
            loading={disabled}
          />
        )}
        renderScheduleCard={(schedule, index) => (
          <DamScheduleViewCard
            schedule={schedule}
            key={index}
            onDelete={() => onClickDeleteSchedule(schedule)}
            onEdit={() => onClickEditSchedule(schedule)}
            hide={hide}
            loading={disabled}
          />
        )}
        addButton={onlyList ? undefined : {
          disabled: !!disabled,
          onClick: onClickAddButton,
        }}
      />
      {pendingProg && (
        <PendingBadge />
      )}
    </>
  );
}
